// Centralized image URL resolution and brand asset references.
//
// Single source of truth for:
//   1. resolveImg(url)      — turns relative `/api/files/...` URLs into
//                              absolute ones via REACT_APP_BACKEND_URL
//   2. PLACEHOLDERS         — per-category fallback for broken/missing images
//   3. BRAND                — locally-hosted, size-optimized logos & assets
//
// Why centralize: prior to this module the URL-prepending logic was copy-pasted
// 38 times across the codebase, with 3 separate definitions of `resolveImg` and
// 10+ raw `<img src={dbField}>` tags that didn't prepend at all. Resulted in:
//   - works on preview (same-origin) but breaks on Capacitor/native (different origin)
//   - works on browsers that allow third-party loads but fails on Safari
//   - any future split-domain production would silently break
//
// Now every image flow ultimately routes through this file.

// ---------- 1) URL resolution ----------
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Turn any incoming image reference into a fully-qualified URL that the
 * browser can fetch from any host (web, PWA, Capacitor).
 *
 *   resolveImg('/api/files/x.png')          → 'https://backend.example.com/api/files/x.png'
 *   resolveImg('https://cdn.example/foo')   → unchanged
 *   resolveImg('data:image/png;base64,...') → unchanged
 *   resolveImg(null)                         → ''
 */
export const resolveImg = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('/api/')) return `${BACKEND}${url}`;
  // Common edge case: DB row sometimes has /api/files/... without /api/ prefix
  if (url.startsWith('/files/')) return `${BACKEND}/api${url}`;
  return url;
};

// ---------- 2) Placeholders (loaded on demand by the <Img> component) ----------
// Inline SVGs would be cleaner but separating them as files keeps bundle size
// down (browser caches them) AND makes them swappable by design without a rebuild.
export const PLACEHOLDERS = Object.freeze({
  avatar:   '/brand/placeholder-avatar.svg',
  business: '/brand/placeholder-business.svg',
  logo:     '/brand/placeholder-business.svg',
  leader:   '/brand/placeholder-avatar.svg',
  event:    '/brand/placeholder-event.svg',
  brand:    '/brand/logo-color.png',
  // Generic catch-all for anything not in the above categories.
  generic:  '/brand/placeholder-business.svg',
});

// ---------- 3) Brand assets — local, optimized ----------
// Sizes after optimization (vs. 2.1 MB raw each):
//   logo-white.png       ~20 KB    (PNG quantized)
//   logo-white.webp      ~30 KB    (WebP — slightly bigger here, but browsers
//                                   transparently pick the smaller of the two)
//   logo-color.png       ~20 KB
//   flyer.png           ~590 KB   (was 42 MB — 99% reduction)
//   flyer.webp          ~158 KB
//
// All paths are relative to /public so they're served by the same origin as
// the React app — no third-party CDN dependency, no cross-origin loads.
export const BRAND = Object.freeze({
  logoWhite:    '/brand/logo-white.png',
  logoWhiteWebp:'/brand/logo-white.webp',
  logoColor:    '/brand/logo-color.png',
  logoColorWebp:'/brand/logo-color.webp',
  logoColorAlt: '/brand/logo-color-alt.png',
  logoColorAltWebp: '/brand/logo-color-alt.webp',
  logoBlackBg:  '/brand/logo-black-bg.png',
  logoBlackBgWebp: '/brand/logo-black-bg.webp',
  leadershipBanner: '/brand/leadership-banner.jpg',
  leadershipBannerWebp: '/brand/leadership-banner.webp',
  flyer:        '/brand/flyer.png',
  flyerWebp:    '/brand/flyer.webp',
  brandManualPdf: '/brand/dccf-manual.pdf',
});
